import { Tooltip, Popconfirm } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { ACTION_BTN_HOVER_CLR } from "@/Constants/common";
import { TOOLTIPTEXT } from "@/utilities/CommonText";

import {
  handleFetchCandidateDetails,
  resendComplianceNotificationToCandiate,
  turnOnOffOverRideComplianceForBankCandidate,
} from "../../../../reducers/clientcandidatedetail";
import { apiDecrypt } from "../../../../utilities/keyEncryption";
import { getComplianceClass } from "../../../../utilities/utils";
import OverrideComplianceOfBank from "../../../Icons/OverrideComplianceOfBank";
import OverrideComplianceOfBankFilled from "../../../Icons/OverrideComplianceOfBankFilled";
import ActionIcons from "../ActionIcons";
import LiveTimerTooltip from "../contracts/Unfilled/LiveTimerTooltip";

export const ComplianceRightActions = () => {
  const dispatch = useDispatch();

  // Redux State
  const { candidateDetails } = useSelector(
    (state) => state.clientCandidateDetail,
  );
  const { isPremiumAccount } = useSelector((state) => state.session);
  const { user } = useSelector((state) => state.clientProfile);
  const { id } = useParams();

  // Destructured candidate details
  const {
    candidate_id,
    invitation_ids,
    is_override_compliance_expiration_period_set,
    override_compliance_expiration_enddate,
    override_compliance_expiration_period_duration,
    override_compliance_expiration_period_type,
    profile_percent,
    is_client_override_compliance_period_set,
    is_bank_candidate,
  } = candidateDetails || {};

  // Local State
  const [reminderLoading, setReminderLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [switchState, setSwitchState] = useState(
    is_override_compliance_expiration_period_set,
  );

  // used for true or false state of switch for compliance override button
  useEffect(() => {
    setSwitchState(is_override_compliance_expiration_period_set);
  }, [is_override_compliance_expiration_period_set]);

  // Handlers
  const handleReminderClick = () => {
    dispatch(
      resendComplianceNotificationToCandiate({
        candidate_id,
        loading: reminderLoading,
        setLoading: setReminderLoading,
      }),
    );
  };

  // Toggle Switch State
  const confirmSwitchToggle = () => {
    setSwitchState(!switchState);
    setShowConfirm(false);
    dispatch(
      turnOnOffOverRideComplianceForBankCandidate({
        status: !switchState ? 1 : 0,
        invitation_ids,
      }),
    );
    dispatch(handleFetchCandidateDetails(apiDecrypt(id)));
  };

  const cancelSwitchToggle = () => setShowConfirm(false);

  const handleSwitchChange = () => setShowConfirm(true);

  // Conditional Display Logic
  const canShowBankComplianceButton =
    isPremiumAccount &&
    user.is_bank_client &&
    is_bank_candidate &&
    (profile_percent != 100 || override_compliance_expiration_enddate !== "") &&
    is_client_override_compliance_period_set;

  const canShowReminderButton =
    isPremiumAccount &&
    user.is_bank_client &&
    is_bank_candidate &&
    profile_percent !== 100;

  return (
    <div className="ComplianceAction_Wrapper flex items-center gap-3">
      <div>
        {override_compliance_expiration_enddate ? (
          <LiveTimerTooltip
            actionedAt=""
            publisheddAt={moment(override_compliance_expiration_enddate).format(
              "DD/MM/YY 23:59",
            )}
            cls="text-red w-[22px] h-[22px]"
            type="invitedTimer"
          />
        ) : null}
      </div>
      <div className="flex h-[40px] cursor-pointer overflow-hidden rounded-lg bg-grey [&>*:last-child]:border-r-0 [&>*]:grid [&>*]:min-w-[56px] [&>*]:place-items-center [&>*]:border-0 [&>*]:border-r [&>*]:border-r-[#E5E6E9] [&>*]:text-txtColor">
        {/* Switch with Confirmation */}
        {canShowBankComplianceButton ? (
          <Popconfirm
            description={
              <div className="max-w-[300px] p-4">
                <p className="text-[16px] font-semibold leading-[normal] text-black">
                  {switchState ? (
                    <>
                      Are you sure you want to remove the candidate’s compliance
                      override? Note that this action will lower the candidate’s
                      profile completion{" "}
                      <span
                        className={`rounded-[4px] px-2 ${getComplianceClass(
                          profile_percent,
                        )}`}
                      >
                        {profile_percent}%
                      </span>{" "}
                      if the required documents are not uploaded.
                    </>
                  ) : (
                    <>
                      Are you sure you wish to override this candidate's
                      compliance? The candidate will get{" "}
                      <span className="pr-1 text-red">
                        {override_compliance_expiration_period_duration}{" "}
                        {override_compliance_expiration_period_type}
                      </span>
                      to submit their compliance document.
                    </>
                  )}
                </p>
              </div>
            }
            okText="Yes"
            cancelText="No"
            placement="bottom"
            open={showConfirm}
            onConfirm={confirmSwitchToggle}
            onCancel={cancelSwitchToggle}
            okButtonProps={{
              className:
                "bg-primary text-white border !border-primary hover:!bg-white hover:!text-primary",
            }}
            cancelButtonProps={{
              className:
                "bg-white text-primary border !border-primary hover:!bg-primary hover:!text-white",
            }}
            icon={null}
          >
            <Tooltip
              title={
                switchState
                  ? TOOLTIPTEXT.removeOverrideCandidateCompliance
                  : TOOLTIPTEXT.overrideCandidateCompliance
              }
            >
              <div
                className={`group ${ACTION_BTN_HOVER_CLR.red} ${switchState ? "bg-[#FCDADA]" : ""}`}
                onClick={handleSwitchChange}
              >
                {React.createElement(
                  switchState
                    ? OverrideComplianceOfBankFilled
                    : OverrideComplianceOfBank,
                  {
                    cls: `w-[22px] h-[22px] ${
                      switchState
                        ? "text-red"
                        : "text-txtColor group-hover:text-red"
                    }`,
                  },
                )}
              </div>
            </Tooltip>
          </Popconfirm>
        ) : null}

        {/* Reminder Button */}
        {canShowReminderButton ? (
          <Tooltip title={TOOLTIPTEXT.reminderToUploadCompliance}>
            <div
              className={`group ${ACTION_BTN_HOVER_CLR.lightBlue}`}
              onClick={handleReminderClick}
            >
              <ActionIcons
                isLoading={reminderLoading}
                type="resendComplianceToCandiate"
              />
            </div>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};
